import { useDispatch, useSelector } from "react-redux";
import Search from "../../../../components/Search";
import { RootState } from "../../../../redux/store";
import { setCurrentPage, setSearchText } from "../../../../redux/historicalSlice";
import { useEffect, useState } from "react";
import ParticipantsTable from "../components/ParticipantsTable";
import { ParticipantDeleteItemDto, ParticipantsActions, ParticipantsListFields } from "../types";
import { useNavigate } from "react-router-dom";
import { deleteLogicParticipant, getParticipants, uploadCsvFile, useParticipantFilters } from "../api/participantsAPI";
import { Row, Col, Button } from "react-bootstrap";
import AlertMessage from "../../../../components/AlertMessage";
import { useAlert } from "../../../../hooks/useAlert";
import ModalGlobal from "../../../../components/AppModal/ModalGlobal";
import { useParticipantModal } from "../hooks/useParticipantModal";
import { useGetCampaigns } from "../hooks/useGetCampaings";
import { UseGetCampaingsObjectResult } from "../../campaign/types";
import { validateFileExtension, validateFileWeight } from "../../../../utils/validators/FileValidator";
import Papa from 'papaparse';
import { SelectedFilter } from "../../../../components/Filters/types";
import { useTranslation } from "react-i18next";
import { exportCsv } from "../api/participantsAPI";
import FiltersContainer from "../../../../components/Filters/components/FiltersContainer";
import { ApiErrorResponse } from "../../../../types/ApiErrorResponse";
import { Pagination, PaginationFilterDto } from "../../../../types/Pagination";
import { useUserPermissions } from "../../../../utils/auth";
import { PERMISSIONS } from "../../../../constants/permissions";

export default function ParticipantsList() {
    const { t } = useTranslation('common');
    const permissions = useUserPermissions();

    //filters
    const { data: filters } = useParticipantFilters();
    
    //actions
    const navigate = useNavigate();

    const handleDeleteParticipant = async (body: ParticipantDeleteItemDto): Promise<ParticipantDeleteReturn> => {
        const result = await deleteLogicParticipant(body);
        await fetchParticipants();
        return result;
    };

    const participantsActions: ParticipantsActions = {
        onDelete: handleDeleteParticipant,
        onView: (id: number) => navigate(`/admin/participants/${id}`)
    };

    const csvColumnKeys = [
        "externalApp",
        "externalAppPatientId",
        "nhc",
        "name",
        "surname",
        "surname2",
        "birthDate",
        "birthCountry",
        "zipCode",
        "city",
        "province",
        "region",
        "country",
        "phoneNumber",
        "email",
        "externalAppCampaignId",
        "externalAppCampaignDescription",
        "externalAppSurveyId",
        "externalAppSurveyDescription",
        "eventDate",
        "medicalActTypeCode",
        "descriptionTypeMedicalAct",
        "externalCenterCode",
        "centerName",
        "externalMedicalServiceCode",
        "medicalServiceDescription",
        "unitCode",
        "unitDescription",
        "floor",
        "block"
    ];

    const csvColumns = csvColumnKeys.map(key =>
        t(`surveyAdmin:form.participants.csvHeaders.${key}`)
    );

    //ReactRedux Toolkit - dispatch 
    const dispatch = useDispatch();
    const currentPage = useSelector((state: RootState) => state.historical.participant.currentPage);
    const itemsPerPage = useSelector((state: RootState) => state.historical.participant.itemsPerPage);
    const searchText = useSelector((state: RootState) => state.historical.participant.searchText);

    const [searchTerm, setSearchTerm] = useState('');

    //handler busqueda
    const handleSearchInput = () => {
        setSearchTerm(searchText || '');
        dispatch(setCurrentPage({ table: "participant", page: 1 }));
    };

    const handleClear = () => {
        dispatch(setCurrentPage({ table: "participant", page: 1 }));
        dispatch(setSearchText({ table: "participant", text: '' }));
        setSearchTerm("");
    }

    //datos participantes
    const [messageError, setMessageError] = useState<string | undefined>('');

    const fetchParticipants = async (page = currentPage, size = itemsPerPage, search = searchTerm, filters = appliedFilters) => {
        const body: PaginationFilterDto = {
            pageNumber: page,
            pageSize: size,
            search,
            filters,
            selectedOrganizationalStructureIds: []
        }

        getParticipants(body).then(data => {
            setParticipantsData(data);
        }).catch((err: ApiErrorResponse) => {
            setMessageError(
                err.message ||
                t("common:requestError.errorBadRequest")
            );
        });
    };

    const [participantsData, setParticipantsData] = useState<Pagination<ParticipantsListFields>>({
        page: 0,
        size: 0,
        totalPages: 0,
        totalRecords: 0,
        items: []
    });

    useEffect(() => {
        fetchParticipants();
    }, [currentPage, itemsPerPage, searchTerm, appliedFilters]);

    //alerta mensaje
    const { alert, hideAlert, isVisible, showAlert } = useAlert({ fromLocation: true });

    //modal 
    const { showModal, modalData, error, endDate, closeModal, setError, openUploadModal, setLoading, isLoading, setModalCount } = useParticipantModal();

    const [showCsvModal, setShowCsvModal] = useState<boolean>(false);

    //recogida campa�as para modal
    const { campaingData } = useGetCampaigns();

    const [selectedFile, setSelectedFile] = useState<File | null>(
        new File([], 'dummy-filename.txt')
    );

    const handleSaveModal = async () => {
        setLoading(true);

        try {
            if (modalData.type === 'upload') {
                if (!selectedCampaing) {
                    setError(t("participant:modal.updateCSVParticipantsErrorMissingCampaing"));
                    return
                }
                if (selectedFile && selectedCampaing) {
                    try {
                        setLoading(true);

                        const response = await uploadCsvFile(selectedFile, selectedCampaing);
                        closeModal();
                        showAlert({
                            message: t("participant:modal.participantSuccesUploadCSVSuccess", { countValues: response.newImportsCount }),
                            type: 'success',
                            duration: 8000
                        });
                        fetchParticipants();
                    } catch (uploadError) {
                        const fetchError = uploadError as ApiErrorResponse;
                        const errorCode = fetchError?.code;
                        if (errorCode === 'PARTICIPANT_ERROR_CVS_DATA') {
                            setError(t("participant:modal.updateCSVParticipantsErrorInCsv"));
                        } else {
                            setError(t("common.requestError.ErrorBadRequest"));
                        }
                    } finally {
                        setLoading(false);
                    }
                }
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    }

  
    const handleExportCsv = async () => {
        setLoading(true);

        try {
            await exportCsv({
                pageNumber: currentPage,
                pageSize: itemsPerPage,
                search: searchText,
                tableOrigin: "participants",
                filters: selectedFilters,
                selectedOrganizationalStructureIds: [],
                filterByOnlyActiveRecords: filterByOnlyActiveRecords
            });

            setShowCsvModal(false);
            showAlert({
                message: t("participant:modal.exportSuccessCSV"),
                type: "success"
            });
        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            const errorMessage = fetchError?.message
            setError(t("common:requestError.ErrorBadRequest") ?? errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = async (file: File | null) => {
        console.log(file);
        setSelectedFile(file);
        setError('');

        if (file) {
            const validationFileExtension = validateFileExtension(file.name);
            const validationFileWeight = validateFileWeight(file.size);

            if (validationFileExtension) {
                setError(t(validationFileExtension));
                return;
            }
            if (validationFileWeight) {
                setError(t(validationFileWeight));
                return;
            }

            const reader = new FileReader();
            reader.onload = async (e) => {
                const text = e.target?.result as string;
                const parsed = Papa.parse(text, {
                    skipEmptyLines: true,
                    delimiter: ';',
                });
                const rowCount = parsed.data.length;
                setModalCount(rowCount);
            };
            reader.readAsText(file);
        }
    };

    const [selectedCampaing, setSelectedCampaing] = useState<string | null>();

    const handleSelectChange = (value: string | null) => {
        setSelectedCampaing(value);
    };

    const [isFilterVisible, setIsFilterVisible] = useState<boolean>(false);

    const handleClick = () => {
        setIsFilterVisible(!isFilterVisible);
    };

    const handleFiltersChange = (filters: SelectedFilter[]) => {
        setAppliedFilters(filters);
        dispatch(setCurrentPage({ table: "participant", page: 1 }));
    };


    return (
        <>
            <Row className="mb-2 align-items-center justify-content-between">
                <Col xs={12} md>
                    <h2>{t("participant:titleParticipant")}</h2>
                </Col>
                <Col xs={12} md="auto" className="d-flex align-items-center justify-content-end gap-2 flex-wrap mt-2 mt-md-0">
                    <Button
                        variant="secondary"
                        style={{ whiteSpace: "nowrap" }}
                        onClick={() => navigate(`/admin/participants/new-participants`)}
                    >
                        {t("participant:newParticipant")}
                    </Button>
                    {permissions.length > 0 && permissions.includes(PERMISSIONS.PARTICIPANTS.IMPORT) && (
                        <Button
                            variant="primary"
                            style={{ whiteSpace: "nowrap" }}
                            onClick={() => openUploadModal()}
                        >
                            {t("participant:buttonImportParticipant")}
                        </Button>
                    )}
                </Col>
            </Row>

            <Row>
                <ModalGlobal<UseGetCampaingsObjectResult>
                    show={showModal}
                    onClose={closeModal}
                    title={modalData.title}
                    contentType={modalData.type}
                    subtitle={modalData.subtitle}
                    saveText={t("participant:modal.modalUpdateCSVParticipantsSend")}
                    closeText={t("participant:modal.modalUpdateCSVParticipantsNoSend")}
                    error={error}
                    endDate={endDate}
                    onSave={() => handleSaveModal()}
                    isLoading={isLoading}
                    handleFileChange={handleFileChange}
                    optionsMap={campaingData}
                    handleSelectChange={handleSelectChange}
                    countValues={modalData.countValues}
                    showOptions={true}
                    handleMotiveDeleteChange={(motive: string | null) => setMotiveDelete(motive)}
                />

                <ModalGlobal<UseGetCampaingsObjectResult>
                    show={showCsvModal}
                    onClose={() => setShowCsvModal(false)}
                    title={t("common:pagination.linkExportCSV")}
                    contentType={"exportCsv"}
                    subtitle={t("participant:modal.participantModalHeaderExport1")}
                    saveText={t("common:modalActionOK")}
                    closeText={t("common:modalActionNOTOK")}
                    error={error}
                    onSave={() => handleExportCsv()}
                    isLoading={isLoading}
                    showOptions = {true}
                />

                <Col className="text-center" xs={12} md={12}>
                    {isVisible && (
                        <AlertMessage
                            message={alert!.message}
                            type={alert!.type}
                            onClose={hideAlert}
                        />
                    )}
                </Col>
                <Col className="text-center" xs={12} md={12}>
                    {messageError && <div className="alert alert-danger">{messageError}
                        <button
                            type="button"
                            className="btn-close btn-sm position-absolute top-0 end-0 m-2"
                            aria-label="Close"
                            onClick={() => setMessageError(undefined)}
                        ></button>
                    </div>}
                </Col>
            </Row>

            <Row className="mb-2">
                <Col xs={12}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ flex: 1 }}>
                            <Search
                                searchText={searchText || ''}
                                onChange={(text) => dispatch(setSearchText({ table: "participant", text: text }))}
                                onSearch={handleSearchInput}
                                onClear={handleClear}
                            />
                        </div>
                        <Button variant="" onClick={handleClick} aria-controls="collapse-filter-id" style={{ border: 0, padding: 0 }}>
                            <img
                                src="/filter.png"
                                alt="Filtar"
                                style={{ width: '40px' }}
                            />
                        </Button>
                    </div>
                </Col>
            </Row>

            {filters && (
                <FiltersContainer
                    filters={filters}
                    isFilterVisible={isFilterVisible}
                    tableKey="participants"
                    historicalKey="participant"
                    onFiltersChange={handleFiltersChange}                    
                />
            )}

            <ParticipantsTable
                participantsData={participantsData}
                currentPage={currentPage}
                pageSize={itemsPerPage}
                totalPages={participantsData.totalPages}
                participantsActions={participantsActions}
                onExportCsv={() => setShowCsvModal(true)}
                
            />            
        </>
    );

}


